import { useEffect } from "react";

/**
 * <JsonLd schemas={[...]} id="..." />
 *
 * Injects one or more JSON-LD blobs into <head> as <script type="application/ld+json">.
 * Multiple separate <script> tags is the Google-recommended pattern — they handle
 * any combination of schemas across the page without merging them into one graph.
 *
 * Each schema gets a unique <script> tag, identified by a deterministic id so that
 * re-renders / SPA route changes replace the same tag instead of duplicating.
 *
 * Usage:
 *   <JsonLd id="page-breadcrumbs" schemas={[breadcrumbSchema('/services')]} />
 *   <JsonLd id="page-service" schemas={[serviceSchema({...})]} />
 *
 * For sitewide schemas (Organization, WebSite, LocalBusiness) call once in Layout.
 */
export default function JsonLd({ id, schemas }) {
  useEffect(() => {
    if (!schemas || !schemas.length) return undefined;
    const cleaned = schemas.filter(Boolean);
    if (!cleaned.length) return undefined;

    // On first mount remove any pre-baked static JSON-LD tags injected by the
    // post-build script (ids beginning with "ld-"). The runtime versions
    // produced by React below are authoritative for the current route.
    document
      .querySelectorAll('script[type="application/ld+json"][id^="ld-"]')
      .forEach((node) => node.parentNode && node.parentNode.removeChild(node));

    // Use one script per id; if multiple schemas pass under the same id we
    // serialize them as separate <script>s with suffixed ids to avoid conflicts.
    const createdNodes = cleaned.map((schema, i) => {
      const elId = cleaned.length === 1 ? id : `${id}-${i}`;
      let el = document.getElementById(elId);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = elId;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
      return el;
    });

    return () => {
      createdNodes.forEach((node) => {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    };
  }, [id, schemas]);

  return null;
}
